import React from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Home, Loader2, Ruler, ShoppingBag } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const QUICK_ROOMS = ['Living Room', 'Primary / Master Bedroom', 'Kitchen', 'Dining Room', 'Home Office', 'Guest Bedroom', "Kids' Bedroom", 'Nursery'];
const slug = (room) => room.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');
const optionCls = 'rounded-lg border border-neutral-200 px-3 py-2.5 text-left text-sm font-medium text-neutral-800 transition-colors hover:border-amber-600 hover:bg-amber-50 disabled:opacity-60';

const RoomPicker = ({ busy, onPick, onOther, onLater }) => (
  <>
    <div className="grid grid-cols-2 gap-2">
      {QUICK_ROOMS.map((room, i) => (
        <motion.button key={room} type="button" onClick={() => onPick(room)} disabled={busy}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i, duration: 0.3 }}
          className={`group flex items-center justify-between gap-2 ${optionCls}`} data-testid={`room-prompt-${slug(room)}`}>
          {room}
          <ShoppingBag className="w-3.5 h-3.5 text-neutral-300 transition-colors group-hover:text-amber-700" />
        </motion.button>
      ))}
    </div>
    <div className="flex items-center justify-between pt-1">
      <button type="button" onClick={onOther} className="text-sm text-amber-700 hover:text-amber-900 underline" data-testid="room-prompt-other">Another room…</button>
      <button type="button" onClick={onLater} className="text-sm text-neutral-500 hover:text-neutral-800" data-testid="room-prompt-later">Not now</button>
    </div>
  </>
);

// After the first add: keep adding windows in the same room, one tap each
const AddedView = ({ room, count, total, busy, sizeLabel, onSameSize, onDifferentSize, onDone }) => (
  <div className="space-y-3" data-testid="room-prompt-added">
    <motion.div key={count} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="w-4 h-4" /></span>
      <p className="text-sm text-emerald-900"><span className="font-semibold" data-testid="room-prompt-added-count">{count} shade{count === 1 ? '' : 's'}</span> in <span className="font-semibold">{room}</span>{total ? <> · <span className="font-semibold" data-testid="room-prompt-room-total">{total}</span></> : null} — in your cart</p>
    </motion.div>
    <p className="text-sm text-neutral-600">More windows in {room}?</p>
    <button type="button" onClick={onSameSize} disabled={busy} className={`w-full flex items-center justify-between gap-2 ${optionCls}`} data-testid="room-prompt-same-size">
      <span className="flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : <Copy className="w-4 h-4 text-amber-600" />} Another window, same size{sizeLabel ? ` (${sizeLabel})` : ''}</span>
      <span className="text-xs text-neutral-400">1 tap</span>
    </button>
    <button type="button" onClick={onDifferentSize} disabled={busy} className={`w-full flex items-center gap-2 ${optionCls}`} data-testid="room-prompt-different-size">
      <Ruler className="w-4 h-4 text-amber-600" /> Another window, different size
    </button>
    <div className="flex justify-end pt-1">
      <button type="button" onClick={onDone} className="rounded-full bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-800" data-testid="room-prompt-done">Done</button>
    </div>
  </div>
);

// Shown right after the price reveal: one tap names the room and adds the shade to the cart
export const RoomPromptDialog = ({ open, onOpenChange, price, onPick, onOther, added, addedCount, addedTotal, busy, sizeLabel, onSameSize, onDifferentSize }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="room-prompt-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Home className="w-5 h-5 text-amber-600" /> {added ? 'Added to your cart' : 'Which room is this for?'}</DialogTitle>
        <DialogDescription>
          {added ? 'Add matching shades for the other windows in this room in one tap.'
            : <>Tap a room and we'll add your shade{price ? <> (<span className="font-semibold text-neutral-900">{price}</span>)</> : null} to the cart — labelled so installation goes smoothly.</>}
        </DialogDescription>
      </DialogHeader>
      {added
        ? <AddedView room={added} count={addedCount} total={addedTotal} busy={busy} sizeLabel={sizeLabel} onSameSize={onSameSize} onDifferentSize={onDifferentSize} onDone={() => onOpenChange(false)} />
        : <RoomPicker busy={busy} onPick={onPick} onOther={onOther} onLater={() => onOpenChange(false)} />}
    </DialogContent>
  </Dialog>
);
